import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
// 3rd party libs
import React, { useEffect, useState } from 'react';
import ParentIcon from '@mui/icons-material/AccountBalanceOutlined';
import AddIcon from '@mui/icons-material/AddRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';

// local imports
import DeleteOrganizationModal from '../../OrganizationsManagement/DeleteOrganizationModal';
import EditOrganizationModal from '../../OrganizationsManagement/EditOrganizationModal';
import { DateFormatter, estimateExpirationDate, estimateTotalUsage, useDimensions } from '../../../helpers';
import styles from './OrganizationCard.module.scss';
import { ORGANIZATION, ROLE_DESCRIPTION, USER_NEW } from '../../../enums/TYPES';
import PeopleCount from '../PeopleCount';
import { MoreIcon } from '../../../assets/icons/threedots';
import EditButton from '../EditButton';
import GigsCount from '../GigsCount';
import Avatar from '../Avatar';
import AssignOrgModal from '../../OrganizationsManagement/AssignOrgModal';
import useOnClickOutside from '../../../helpers/useOnClickOutside';
import { ContainedButton } from '../Buttons';
import { getCSVforCurrentPeriodVideofeeds } from '../../../api';
import TranscodedFeedsCSVModal from './TranscodedFeedsCSVModal';
import { API_ROUTES } from '../../../enums/API_ROUTES';
import { saveAs } from 'file-saver';
import { getTokenFromLocalStorage } from '../../../helpers/localStorage';
import PublicDownloadLinkExpiryModal from '../../../hoc/Layout/Header/PublicDownloadLinkExpiryModal';
import StorageSummaryTooltip from '../../OrganizationsManagement/S3StorageDetails/StorageSummaryTooltip';
import { PATHS } from '../../../enums/PATHS';
import { Portal } from '../../../helpers/bodyPortal';
import { useOverflowAwareActionMenu } from '../../../helpers/useOverflowAwareActionMenu';
import { useAppSelector } from '../../../store/hooks';
import { ROLES } from '../../../enums/ROLES';

export type User = {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  avatar_url: string;
  last_online?: string;
  created_at?: string;
  need_help?: string;
  invitation_token: string;
  organization_name?: string;
  organization: ORGANIZATION;
  organizations: ORGANIZATION[];
  organization_users?: {
    username: string;
    role: ROLE_DESCRIPTION;
    organization: ORGANIZATION;
    id: string;
  }[];
};

type UserRole = {
  id: string;
  title: string;
  description: string;
};

type TOrganizationCard = {
  data: ORGANIZATION;
  fetchAll?: () => Promise<unknown>;
  onOrgSelect: (org: ORGANIZATION | null, mouseClicked?: boolean) => void;
  selectedOrg: ORGANIZATION | null;
  loading: boolean;
  viewMode?: boolean;
};

const OrganizationCard: React.FC<TOrganizationCard> = ({ data, fetchAll, onOrgSelect, selectedOrg, loading, viewMode }) => {
  const archived = data.deletion_status === 'ARCHIVED';
  const canViewEmails = useCanViewEmails();
  const history = useHistory();
  const isSuperAdmin = useAppSelector((state) => state.user.role === ROLES.SUPER_ADMIN);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [editData, setEditData] = useState(data);
  const { widthD } = useDimensions();

  useEffect(() => {
    setEditData(data);
  }, [data]);

  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ x: number; y: number } | null>(null);
  const { actionMenuRef, triggerRef, menuPosition } = useOverflowAwareActionMenu({
    showMenu: showDotsMenu,
    anchorPosition: menuAnchorPosition,
  });

  const handleDeleteClick = (e: any) => {
    e.stopPropagation();
    if (loading) {
      return;
    }
    setShowDotsMenu(false);
    setShowDeleteModal(true);
  };

  const onEditModalClose = () => {
    setShowEditModal(false);
    onOrgSelect(null);
  };

  const onEditModalOpen = (e: any) => {
    if (loading) {
      return;
    }

    e.stopPropagation();
    setShowDotsMenu(false);
    setShowEditModal(true);
  };

  const onDeleteModalClose = () => {
    setShowDeleteModal(false);
    onOrgSelect(null);
  };

  const onSelect = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!archived) onOrgSelect(data, true);
  };

  const [showAssignOrgModal, setShowAssignOrgModal] = useState(false);
  const onAssignOrgClick = (e?: any) => {
    e?.stopPropagation();
    setShowDotsMenu(false);
    setShowAssignOrgModal(!showAssignOrgModal);
  };

  const storagePct = (data.storage_usage / data.storage_limit) * 100;
  const hoursPct = (data.hours_limit_used / data.hours_limit) * 100;
  const downloadHoursPct = data.download_hours_limit
    ? (data.download_hours_limit_used / data.download_hours_limit) * 100
    : 0;
  const downloadMinutesUsed = Math.round(data.download_hours_limit_used * 60);
  const showLocation = widthD > 1024 && !selectedOrg;

  const getColor = (pctOrRem: number, isRem = false) => {
    if (isRem) {
      if (pctOrRem === 0) return;
      if (pctOrRem < 0) return styles.redText;
      if (pctOrRem > 0 && pctOrRem < 10) return styles.amberText;
      return styles.greenText;
    }
    if (pctOrRem >= 90) return styles.redText;
    if (pctOrRem >= 80) return styles.amberText;
    return '';
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    if (selectedOrg && +selectedOrg.id !== +data.id) {
      onSelect(e as any);
    }

    e.preventDefault();
    e.stopPropagation();

    if (showDotsMenu) {
      setShowDotsMenu(false);
      setMenuAnchorPosition(null);
      return;
    }

    setMenuAnchorPosition(e.type === 'contextmenu' ? { x: e.clientX, y: e.clientY } : null);
    setShowDotsMenu(true);
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
      setMenuAnchorPosition(null);
    }
  });

  const [openCSVModal, setOpenCSVModal] = useState(false);
  const switchCsvModal = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setOpenCSVModal((v) => !v);
  };
  const getCsvUrl = (org: ORGANIZATION) => `${process.env.REACT_APP_API_URL}${API_ROUTES.ORGANIZATION_VIDEOFEED_CSV}${org.id}`;
  const [openExternalShareEmailsCsvModal, setOpenExternalShareEmailsCsvModal] = useState(false);
  const switchExternalShareEmailsCsvModal = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setOpenExternalShareEmailsCsvModal((v) => !v);
  };
  const getExternalShareEmailsCsvUrl = (org: ORGANIZATION) =>
    `${process.env.REACT_APP_API_URL}${API_ROUTES.ORGANIZATION_EXTERNAL_SHARE_EMAILS_CSV}${org.id}`;

  const onDownloadExternalShareEmailsCsv = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);

    try {
      const token = getTokenFromLocalStorage();
      const res = await fetch(getExternalShareEmailsCsvUrl(data), {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: 'text/csv',
        },
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const blob = await res.blob();
      saveAs(blob, `external-share-emails-org-${data.id}.csv`);
    } catch (err) {
      console.error('External share emails CSV download failed:', err);
    }
  };

  const onOpenCsvClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenCSVModal(true);
  };

  const onGenerateCsv = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await getCSVforCurrentPeriodVideofeeds(data);
    } catch (err) {
      console.error('CSV download failed:', err);
    }
  };

  const openS3StorageDetails = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    history.push(PATHS.SA_ORGANIZATION_STORAGE.path.replace(':organizationId', String(data.id)));
  };

  return (
    <>
      <div
        className={cx(
          styles.card,
          styles.gridRow,
          showLocation ? styles.withLocation : styles.compact,
          selectedOrg && (selectedOrg.id === data.id ? styles.selectedOrg : styles.notSelectedOrg),
        )}
        onClick={onSelect}
        onContextMenu={switchDotsMenu}
        style={{ position: 'relative' }}
      >
        <Tooltip title={data.type === 'parent' ? `"${data.name}" is parent organisation` : data.name} placement="bottom-start">
          <div className={styles.title}>
            <Avatar avatar={data.image_url} name={data.name} />
            {data.type === 'parent' && <ParentIcon className={styles.parentIcon} />}
            <span className={styles.name}>{data.name}</span>
          </div>
        </Tooltip>

        {showLocation && (
          <div className={styles.location}>
            <Tooltip title={data.location}>
              <span className={styles.locationName}>{data.location}</span>
            </Tooltip>
          </div>
        )}

        <StorageSummaryTooltip organization={data}>
          <div className={cx(styles.storage, getColor(storagePct))}>
            <GigsCount
              enforcePercentage={!!selectedOrg}
              widthD={widthD}
              withPercentage={true}
              usage={data.storage_usage}
              limit={data.storage_limit}
              disableTooltip
            />
          </div>
        </StorageSummaryTooltip>

        <Tooltip
          style={{ background: 'transparent' }}
          placement="top"
          title={archived ? '' : (
            <div style={{ padding: '4px', width: '152px' }}>
              Generate a CSV of all videofeeds from this period — including deleted (archived)
              <br />
              <ContainedButton style={{ marginTop: 6, width: 152 }} id={`${data.id}-open`} onClick={onOpenCsvClick}>
                Open CSV
              </ContainedButton>
              <ContainedButton style={{ marginTop: 6, width: 152 }} id={`${data.id}-download`} onClick={onGenerateCsv}>
                Download CSV
              </ContainedButton>
            </div>
          )}
        >
          <div className={cx(styles.storage, getColor(hoursPct))}>
            <GigsCount
              enforcePercentage={!!selectedOrg}
              widthD={widthD}
              withPercentage
              hours
              usage={+data.hours_limit_used.toFixed(1)}
              limit={data.hours_limit}
            />
          </div>
        </Tooltip>

        <Tooltip placement="top" title={`Download hours (transcoded): ${downloadMinutesUsed} min`}>
          <div className={cx(styles.storage, getColor(downloadHoursPct))}>
            <GigsCount
              enforcePercentage={!!selectedOrg}
              widthD={widthD}
              withPercentage
              hours
              usage={+data.download_hours_limit_used.toFixed(1)}
              limit={data.download_hours_limit}
            />
          </div>
        </Tooltip>

        <Tooltip
          placement="top"
          title={
            <>
              {DateFormatter(data.period_start)} - {DateFormatter(data.period_end)}
            </>
          }
        >
          <div className={styles.date}>{DateFormatter(data.period_end)}</div>
        </Tooltip>

        <div className={cx(styles.estim, styles.storage, getColor(data.projected_hours_at_the_end, true))}>
          {data.projected_hours_at_the_end}
        </div>

        <div className={styles.userLimit}>
          <Tooltip
            placement="right"
            arrow
            title={
              <div>
                {data.organization_users?.map((u: any) => (
                  <div key={u.id}>{u.user.username || (canViewEmails ? u.user.email : 'User')}</div>
                ))}
              </div>
            }
          >
            <PeopleCount count={data.organization_users?.length} limit={data.users_limit} />
          </Tooltip>
        </div>

        <div className={styles.created}>
          <span>{DateFormatter(data.created_at)}</span>
        </div>

        {viewMode && (
          <div>
            {data.hours_limit_used.toFixed(1)} / {data.hours_limit}
            {' | '}
            {data.download_hours_limit_used.toFixed(1)} / {data.download_hours_limit}
          </div>
        )}

        <div className={styles.actionBtns}>
          <div ref={triggerRef} className={styles.dotIcon} onClick={switchDotsMenu}>
            <MoreIcon />
          </div>
        </div>

        <Portal>
          <div
            ref={actionMenuRef}
            className={styles.actionMenu}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              display: showDotsMenu ? 'block' : 'none',
              top: `${menuPosition.y}px`,
              left: `${menuPosition.x}px`,
              right: 'auto',
            }}
          >
            {archived ? (
              <div className={styles.menuOption} onClick={handleDeleteClick}>
                <RestoreIcon className={styles.addIcon} />
                <span>Restore organisation</span>
              </div>
            ) : (
              <>
                <div className={styles.menuOption} onClick={openS3StorageDetails}>
                  <StorageOutlinedIcon className={styles.addIcon} />
                  <span>View S3 storage details</span>
                </div>

                <div
                  className={styles.menuOption}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDotsMenu(false);
                    setShowExpiryModal(true);
                  }}
                >
                  <SettingsOutlinedIcon className={styles.addIcon} />
                  <span>Public Link Expiry</span>
                </div>


                <div
                  className={styles.menuOption}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDotsMenu(false);
                    onEditModalOpen(e);
                  }}
                >
                  <EditButton className={styles.edit} />
                  <span>Edit organisation</span>
                </div>

                <div
                  className={styles.menuOption}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteClick(e);
                  }}
                >
                  <ArchiveOutlinedIcon className={styles.addIcon} />
                  <span>Archive organisation</span>
                </div>

                <div
                  className={styles.menuOption}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAssignOrgClick(e);
                  }}
                >
                  <AddIcon className={styles.addIcon} />
                  <span>{data.type === 'parent' ? 'Assign child organisation' : 'Assign parent organisation'}</span>
                </div>

                <div
                  className={styles.menuOption}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDotsMenu(false);
                    setOpenExternalShareEmailsCsvModal(true);
                  }}
                >
                  <DescriptionOutlinedIcon className={styles.addIcon} />
                  <span>Open external share emails CSV</span>
                </div>

                <div
                  className={styles.menuOption}
                  onClick={onDownloadExternalShareEmailsCsv}
                >
                  <DescriptionOutlinedIcon className={styles.addIcon} />
                  <span>Download external share emails CSV</span>
                </div>
              </>
            )}
          </div>
        </Portal>
      </div>

      {openCSVModal && (
        <TranscodedFeedsCSVModal
          open={openCSVModal}
          onClose={switchCsvModal}
          src={getCsvUrl(data)}
          downloadFilename={`${data.name} ${data.period_start}_${data.period_end}.csv`}
        />
      )}

      {openExternalShareEmailsCsvModal && (
        <TranscodedFeedsCSVModal
          open={openExternalShareEmailsCsvModal}
          onClose={switchExternalShareEmailsCsvModal}
          src={getExternalShareEmailsCsvUrl(data)}
          title="External share emails (CSV)"
          showControls={false}
          downloadFilename={`external-share-emails-org-${data.id}.csv`}
        />
      )}

      {showEditModal && !viewMode && (
        <EditOrganizationModal fetchAll={fetchAll} switchModal={onEditModalClose} open={showEditModal} organization={editData} />
      )}

      {!viewMode && <DeleteOrganizationModal fetchAll={fetchAll} switchModal={onDeleteModalClose} open={showDeleteModal} id={data.id} restore={archived} />}

      {!viewMode && <AssignOrgModal open={showAssignOrgModal} switchModal={onAssignOrgClick} fetchAll={fetchAll} organization={data} />}

      <PublicDownloadLinkExpiryModal
        open={showExpiryModal}
        onClose={() => setShowExpiryModal(false)}
        organizationId={String(data.id)}
        organizationName={data.name}
      />
    </>
  );
};

export default OrganizationCard;
